import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Segment, Button, Message, Checkbox, Icon } from 'semantic-ui-react'
import L from 'leaflet'

import Summary from '../Directions/Summary'
import Maneuvers from '../Directions/Maneuvers'
import { downloadFile } from 'actions/commonActions'
import { showProvider } from 'actions/directionsActions'
import { toggleGPSTrace } from 'actions/analyzeActions'
import jsonFormat from 'json-format'
import { jsonConfig } from 'Controls/settings-options'

import { withAuth } from 'utils/withAuth'

const ANALYZE_TRACE_PROVIDER = 'ANALYZE_TRACE'

class AnalyzeOutputControl extends React.Component {
  static propTypes = {
    analyze: PropTypes.object,
    profile: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
    results: PropTypes.object,
    idx: PropTypes.number,
  }

  constructor(props) {
    super(props)
    const { analyze } = this.props
    const traceResult = analyze?.traceRouteResult

    let alternates = []
    if (traceResult?.data?.alternates) {
      alternates = traceResult.data.alternates.map((_, i) => i)
    }

    this.state = {
      showResults: {
        '-1': false,
        ...alternates.reduce((acc, v) => ({ ...acc, [v]: false }), {}),
      },
    }
    this.showManeuvers = this.showManeuvers.bind(this)
  }

  componentDidUpdate(prevProps) {
    const { analyze } = this.props
    const traceResult = analyze?.traceRouteResult
    const prevTraceResult = prevProps.analyze?.traceRouteResult

    // Update state when trace result changes
    if (traceResult !== prevTraceResult && traceResult?.data?.alternates) {
      const alternates = traceResult.data.alternates.map((_, i) => i)
      this.setState({
        showResults: {
          '-1': false,
          ...alternates.reduce((acc, v) => ({ ...acc, [v]: false }), {}),
        },
      })
    }
  }

  showManeuvers(idx) {
    this.setState({
      showResults: {
        ...this.state.showResults,
        [idx]: !this.state.showResults[idx],
      },
    })
  }

  handleToggleGPSTrace = (event, data) => {
    const { dispatch } = this.props
    dispatch(toggleGPSTrace(data.checked))
  }

  dateNow() {
    let dtNow = new Date()
    dtNow =
      [dtNow.getMonth() + 1, dtNow.getDate(), dtNow.getFullYear()].join('/') +
      '_' +
      [dtNow.getHours(), dtNow.getMinutes(), dtNow.getSeconds()].join(':')
    return dtNow
  }

  exportToJson = (e) => {
    const { analyze } = this.props
    const traceResult = analyze?.traceRouteResult
    if (!traceResult?.data) {
      return
    }

    const formattedData = jsonFormat(traceResult.data, jsonConfig)
    e.preventDefault()
    downloadFile({
      data: formattedData,
      fileName: 'valhalla-trace-route_' + this.dateNow() + '.json',
      fileType: 'text/json',
    })
  }

  exportToGeoJson = (e) => {
    const { analyze } = this.props
    const traceResult = analyze?.traceRouteResult
    if (!traceResult?.data?.decodedGeometry) {
      return
    }

    const coordinates = traceResult.data.decodedGeometry
    const formattedData = jsonFormat(
      L.polyline(coordinates).toGeoJSON(),
      jsonConfig
    )
    e.preventDefault()
    downloadFile({
      data: formattedData,
      fileName: 'valhalla-trace-route_' + this.dateNow() + '.geojson',
      fileType: 'text/json',
    })
  }

  handleChange = (event, data) => {
    const { dispatch, idx } = this.props
    dispatch(showProvider(ANALYZE_TRACE_PROVIDER, data.checked, idx))
  }

  render() {
    const { analyze } = this.props
    const { traceRouteResult, successful, parsedData } = analyze || {}

    if (!parsedData) {
      return (
        <Segment style={{ maxWidth: '100%', overflow: 'hidden' }}>
          <Message info>
            <Message.Header>No Route Data</Message.Header>
            <p>Upload a GPX or CSV file to trace route.</p>
          </Message>
        </Segment>
      )
    }

    if (!traceRouteResult?.data) {
      return (
        <Segment style={{ maxWidth: '100%', overflow: 'hidden' }}>
          <Message info>
            <Message.Header>No Trace Results</Message.Header>
            <p>Click &quot;Analyze Route&quot; to trace the uploaded route.</p>
          </Message>
        </Segment>
      )
    }

    const data = traceRouteResult.data
    if (!data.trip) {
      return (
        <Segment style={{ maxWidth: '100%', overflow: 'hidden' }}>
          <Message warning>
            <Message.Header>No Route Found</Message.Header>
            <p>Could not trace the route from the uploaded data.</p>
          </Message>
        </Segment>
      )
    }

    // Build alternates list
    let alternates = []
    if (data.alternates) {
      alternates = data.alternates.map((alternate, i) => {
        const legs = alternate.trip.legs
        // Use method to determine header - if user_driven, label as "Traced Route", otherwise "Alternate"
        const header =
          alternate.trip?.summary_dp?.method === 'user_driven'
            ? 'Traced Route'
            : `Alternate ${i + 1}`
        return (
          <Segment
            key={`alternate_${i}`}
            style={{
              margin: '0 1rem 10px',
              display: successful ? 'block' : 'none',
            }}
          >
            <div className={'flex-column'}>
              <Summary
                header={header}
                idx={i}
                summary={alternate.trip.summary}
                summary_dp={alternate.trip.summary_dp}
                provider={ANALYZE_TRACE_PROVIDER}
              />
              <div className={'flex justify-between'}>
                <Button
                  size="mini"
                  toggle
                  active={this.state.showResults[i]}
                  onClick={() => this.showManeuvers(i)}
                >
                  {this.state.showResults[i]
                    ? 'Hide Maneuvers'
                    : 'Show Maneuvers'}
                </Button>
                <div className={'flex'}>
                  <div
                    className={'flex pointer'}
                    style={{ alignSelf: 'center' }}
                    onClick={this.exportToJson}
                  >
                    <Icon circular name={'download'} />
                    <div className={'pa1 b f6'}>{'JSON'}</div>
                  </div>
                  <div
                    className={'ml2 flex pointer'}
                    style={{ alignSelf: 'center' }}
                    onClick={this.exportToGeoJson}
                  >
                    <Icon circular name={'download'} />
                    <div className={'pa1 b f6'}>{'GeoJSON'}</div>
                  </div>
                </div>
              </div>

              {this.state.showResults[i] ? (
                <div className={'flex-column'}>
                  <Maneuvers legs={legs} idx={i} unit={data.trip.units} />
                </div>
              ) : null}
            </div>
          </Segment>
        )
      })
    }

    return (
      <>
        {/* GPS Trace Toggle */}
        {successful && parsedData && (
          <Segment
            style={{
              margin: '0 1rem 10px',
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            <div className={'flex justify-between'}>
              <span className="b">GPS Trace</span>
              <Checkbox
                slider
                label={'Map'}
                checked={analyze?.showGPSTrace ?? true}
                onChange={this.handleToggleGPSTrace}
              />
            </div>
          </Segment>
        )}

        {/* Main Route */}
        <Segment
          style={{
            margin: '0 1rem 10px',
            display: successful ? 'block' : 'none',
          }}
        >
          <div className={'flex-column'}>
            <Summary
              header={
                data.trip?.summary_dp?.method === 'user_driven'
                  ? 'Traced Route'
                  : 'Directions'
              }
              summary={data.trip.summary}
              summary_dp={data.trip.summary_dp}
              idx={-1}
              provider={ANALYZE_TRACE_PROVIDER}
            />
            <div className={'flex justify-between'}>
              <Button
                size="mini"
                toggle
                active={this.state.showResults[-1]}
                onClick={() => this.showManeuvers(-1)}
              >
                {this.state.showResults[-1]
                  ? 'Hide Maneuvers'
                  : 'Show Maneuvers'}
              </Button>
              <div className={'flex'}>
                <div
                  className={'flex pointer'}
                  style={{ alignSelf: 'center' }}
                  onClick={this.exportToJson}
                >
                  <Icon circular name={'download'} />
                  <div className={'pa1 b f6'}>{'JSON'}</div>
                </div>
                <div
                  className={'ml2 flex pointer'}
                  style={{ alignSelf: 'center' }}
                  onClick={this.exportToGeoJson}
                >
                  <Icon circular name={'download'} />
                  <div className={'pa1 b f6'}>{'GeoJSON'}</div>
                </div>
              </div>
            </div>

            {this.state.showResults[-1] ? (
              <div className={'flex-column'}>
                <Maneuvers
                  legs={data.trip ? data.trip.legs : undefined}
                  idx={-1}
                  unit={data.trip.units}
                />
              </div>
            ) : null}
          </div>
        </Segment>
        {alternates.length ? alternates : ''}
      </>
    )
  }
}

const mapStateToProps = (state) => {
  const { profile } = state.common
  const { analyze } = state
  // Get results for ANALYZE_TRACE provider (similar to directions)
  const results = {
    [ANALYZE_TRACE_PROVIDER]: {
      data: analyze?.traceRouteResult?.data || {},
      show: analyze?.traceRouteResult
        ? {
            '-1': true,
            ...(analyze.traceRouteResult.data?.alternates || []).reduce(
              (acc, _, i) => ({ ...acc, [i]: true }),
              {}
            ),
          }
        : {},
    },
  }
  return {
    profile,
    analyze,
    results,
  }
}

export default connect(mapStateToProps)(withAuth(AnalyzeOutputControl))
